import React, { useMemo, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { TICK_FONT, ceilNice } from './config';
import { useToggleMap } from './Hooks/useToggleMap';
import { formatAmount } from '/src/lib/helpers';
import { ChartTooltip, createExternalTooltip } from './ChartTooltip';
import { Pill } from './Legend';

export default function BarWithLineChart({ data }) {
    const [hidden, toggleDataset] = useToggleMap({ 0: false, 1: false });
    const [tooltipState, setTooltipState] = useState({ visible: false });

    const barColor  = data?.color_table?.[0] || '#b5002a';
    const lineColor = data?.color_table?.[1] || '#FF8811';

    const chartData = useMemo(() => {
        if (!data?.dataset) return null;
        const { dataset, line_width, dot_radius, smooth_line, cornered, x_padding, max_bar_width } = data;
        return {
            labels: dataset.map(d => d[0]),
            datasets: [
                {
                    type: 'bar',
                    data: dataset.map(d => d[2]),
                    backgroundColor: barColor,
                    borderRadius: cornered ? { topLeft: 10, topRight: 10 } : 0,
                    borderSkipped: 'bottom',
                    yAxisID: 'yLeft',
                    maxBarThickness: max_bar_width || 60,
                    categoryPercentage: 1 - (x_padding || 0.15),
                    barPercentage: 1,
                    order: 2,
                    hidden: !!hidden?.[0],
                },
                {
                    type: 'line',
                    data: dataset.map(d => d[1]),
                    borderColor: lineColor,
                    backgroundColor: lineColor,
                    borderWidth: line_width || 2,
                    pointRadius: dot_radius || 5,
                    pointBackgroundColor: lineColor,
                    tension: smooth_line ? 0.4 : 0,
                    cubicInterpolationMode: 'monotone',
                    yAxisID: 'yRight',
                    order: 1,
                    hidden: !!hidden?.[1],
                },
            ],
        };
    }, [data, hidden, barColor, lineColor]);

    const options = useMemo(() => {
        if (!data?.dataset) return {};
        const { dataset, n_axis_intervals = 3, show_grids, show_yAxis, tooltip_title_prefix, tooltip_labels } = data;

        const maxDeals   = Math.max(...dataset.map(d => d[2]), 0);
        const maxCapital = Math.max(...dataset.map(d => d[1]), 0);

        const leftStep  = Math.max(1, Math.ceil(maxDeals / n_axis_intervals));
        const leftMax   = leftStep * n_axis_intervals;
        const leftTicks = Array.from({ length: n_axis_intervals + 1 }, (_, i) => i * leftStep);

        const rightStep  = ceilNice(maxCapital / n_axis_intervals);
        const rightMax   = rightStep * n_axis_intervals;
        const rightTicks = Array.from({ length: n_axis_intervals + 1 }, (_, i) => i * rightStep);

        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 800 },
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: false,
                    mode: 'index',
                    intersect: false,
                    external: createExternalTooltip(setTooltipState, (item) => ({
                        title: `${tooltip_title_prefix ?? 'Year'}: ${item.label}`,
                        lines: [
                            `${tooltip_labels?.[0] ?? 'Deals'}: ${item.chart.data.datasets[0].data[item.dataIndex]}`,
                            `${tooltip_labels?.[1] ?? 'Cumulative Capital'}: ${formatAmount(item.chart.data.datasets[1].data[item.dataIndex])}`,
                        ],
                    })),
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    border: { color: '#ddd' },
                    ticks: { color: '#888', font: TICK_FONT },
                },
                yLeft: {
                    type: 'linear',
                    position: 'left',
                    display: show_yAxis !== false,
                    min: 0,
                    max: leftMax,
                    afterBuildTicks: scale => { scale.ticks = leftTicks.map(v => ({ value: v })); },
                    border: { display: false },
                    grid: { drawOnChartArea: false },
                    ticks: { color: '#888', font: TICK_FONT, padding: 8 },
                },
                yRight: {
                    type: 'linear',
                    position: 'right',
                    display: show_yAxis !== false,
                    min: 0,
                    max: rightMax,
                    afterBuildTicks: scale => { scale.ticks = rightTicks.map(v => ({ value: v })); },
                    border: { display: false },
                    grid: { drawOnChartArea: !!show_grids, color: '#f2f2f2' },
                    ticks: { color: '#888', font: TICK_FONT, padding: 8, callback: v => formatAmount(v) },
                },
            },
        };
    }, [data]);

    if (!chartData) return null;

    const legendLabels = data.legend_keys || ['Deals/yr', 'Cumulative Capital'];

    return (
        <div className="chart-container">
            <div className="chart-legend-row" style={{ marginBottom: 12 }}>
                <Pill label={legendLabels[0]} swatch={barColor}  isHidden={hidden[0]} onClick={() => toggleDataset(0)} />
                <Pill label={legendLabels[1]} swatch={lineColor} isHidden={hidden[1]} onClick={() => toggleDataset(1)} />
            </div>

            <div className="chart-canvas" style={{ height: data.height || 240 }}>
                <Chart type="bar" data={chartData} options={options} />
                <ChartTooltip state={tooltipState} />
            </div>
        </div>
    );
}
